<script lang="ts">
import { computed, ComputedRef, ref } from 'vue'
import { useDesignStore } from '../../../stores/designStore.ts'

import CustomModal from '@/components/CustomModal.vue'
import { useUserStore } from '@/stores/userStore.ts'
import { Ref } from 'vue'

import BForm from 'bootstrap-vue-next/src/components/BForm/BForm.vue'
import BasicInput from '../BasicInput.vue'
import HpWidgetModal from './HpWidgetModal.vue'

export default {
  props: [
    'setCurrentAndBarrier',
    'totalHp',
    'traits',
    'statusEffects',
    'hpStatusModifiers',
    'storeRef',
    'currentHp',
    'barrierHp',
    'wornArmorPassives',
    'secondaryHandheldPassives',
    'primaryHandheldPassives',
    'addNewHpStatusModifier',
    'removeHpStatusModifier',
    'isEditing',
    'setHp',
    'simplifyView'
  ],
  setup(props, context) {
    const modal = ref(false)
    const userStore = useUserStore()
    const designStore = useDesignStore()
    const damage: Ref<number> = ref(0)
    const dice: Ref<number> = ref(0)
    const heal: Ref<number> = ref(0)
    const barrier: Ref<number> = ref(0)
    const currentHpCopy = ref(props.currentHp)
    const currentBarrierCopy = ref(props.barrierHp)
    const damageTypes = [
      'Un-typed',
      'Fire',
      'Ice',
      'Lightning',
      'Force',
      'Toxic',
      'Stress',
      'Piercing',
      'Edged',
      'Blunt',
      'Blight',
      'Divine',
      'Pure Magic'
    ]

    const modifierType = [
      'Rot',
      'Suffering',
      'Hp Regen',
      'Barrier Regen',
      'Modify Base Hp',
      'Override Base Hp'
    ]

    const damageType = ref('un-typed')

    function getTotalColor() {
      if (
        props.hpStatusModifiers['Override Base Hp'] ||
        props.hpStatusModifiers['Modify Base Hp']
      ) {
        return designStore.alertTheme
      }
      return designStore.primaryText
    }

    function getBarrierColor() {
      if (props.hpStatusModifiers['Barrier Regen']) {
        return designStore.alertTheme
      }
      return designStore.secondaryTheme
    }

    function getCurrentColor() {
      if (
        props.hpStatusModifiers['Rot'] ||
        props.hpStatusModifiers['Hp Regen'] ||
        props.hpStatusModifiers['Suffering'] ||
        props.statusEffects['Suffering']
      ) {
        return designStore.alertTheme
      }
      return designStore.primaryText
    }

    const statusModifiersList: ComputedRef<Array<any>> = computed(() => {
      let modifiers = Object.values(props.hpStatusModifiers)
      let ret = []
      modifiers.forEach((modGroup: any) => {
        ret = ret.concat(Object.values(modGroup))
      })

      return ret
    })

    function addHpStatusModifier(addedVal: {
      selectedMod: string
      linkedStatus: string
      modAmount: number
      sufferingDamageType: string
    }) {
      let statusObj = {
        modifierType: addedVal.selectedMod,
        linkedStatus: addedVal.linkedStatus,
        modAmount: addedVal.modAmount,
        sufferingDamageType: addedVal.sufferingDamageType
      }
      props.addNewHpStatusModifier(statusObj)
    }

    function dealDamage() {
      let finalBarrier = props.barrierHp
      let finalCurrent = props.currentHp

      finalBarrier = finalBarrier - getRealDamageValue(damageType.value, damage.value)
      if (finalBarrier < 0) {
        finalCurrent += finalBarrier
        finalBarrier = 0
      }
      props.setCurrentAndBarrier(finalCurrent, finalBarrier)
    }

    function getRealDamageValue(damageType: string, damage: number) {
      if (damageType === 'Un-Typed') {
        return damage
      }
      let resistance =
        props.traits[damageType + ' Resistance'] ||
        props.statusEffects[damageType + ' Resistance'] ||
        props.secondaryHandheldPassives[damageType + ' Resistance'] ||
        props.primaryHandheldPassives[damageType + ' Resistance'] ||
        props.wornArmorPassives[damageType + ' Resistance']
      let susceptibility =
        props.traits[damageType + ' Susceptibility'] ||
        props.statusEffects[damageType + ' Susceptibility'] ||
        props.secondaryHandheldPassives[damageType + ' Susceptibility'] ||
        props.primaryHandheldPassives[damageType + ' Susceptibility'] ||
        props.wornArmorPassives[damageType + ' Susceptibility']
      let immunity =
        props.traits[damageType + ' Immunity'] ||
        props.statusEffects[damageType + ' Immunity'] ||
        props.secondaryHandheldPassives[damageType + ' Immunity'] ||
        props.primaryHandheldPassives[damageType + ' Immunity'] ||
        props.wornArmorPassives[damageType + ' Immunity']
      let vulnerability =
        props.traits[damageType + ' Vulnerability'] ||
        props.statusEffects[damageType + ' Vulnerability'] ||
        props.secondaryHandheldPassives[damageType + ' Vulnerability'] ||
        props.primaryHandheldPassives[damageType + ' Vulnerability'] ||
        props.wornArmorPassives[damageType + ' Vulnerability']
      let damageReduction =
        props.traits[damageType + ' Damage Reduction'] ||
        props.statusEffects[damageType + ' Damage Reduction'] ||
        props.secondaryHandheldPassives[damageType + ' Damage Reduction'] ||
        props.primaryHandheldPassives[damageType + ' Damage Reduction'] ||
        props.wornArmorPassives[damageType + ' Damage Reduction']
      let damageAmplification =
        props.traits[damageType + ' Damage Amplification'] ||
        props.statusEffects[damageType + ' Damage Amplification'] ||
        props.secondaryHandheldPassives[damageType + 'Damage Amplification'] ||
        props.primaryHandheldPassives[damageType + ' Damage Amplification'] ||
        props.wornArmorPassives[damageType + ' Damage Amplification']
      if (props.statusEffects['Petrified']) {
        if (
          damageType == 'Fire' ||
          damageType == 'Ice' ||
          damageType == 'Lightning' ||
          damageType == 'Force' ||
          damageType == 'Toxic' ||
          damageType == 'Piercing' ||
          damageType == 'Edged'
        ) {
          resistance = true
        }
      }
      if (
        damageType == 'Fire' ||
        damageType == 'Ice' ||
        damageType == 'Lightning' ||
        damageType == 'Force'
      ) {
        resistance =
          resistance ||
          props.traits['Elemental Resistance'] ||
          props.secondaryHandheldPassives['Elemental Resistance'] ||
          props.primaryHandheldPassives['Elemental Resistance'] ||
          props.wornArmorPassives['Elemental Resistance']
        susceptibility =
          susceptibility ||
          props.traits['Elemental Susceptibility'] ||
          props.secondaryHandheldPassives['Elemental Susceptibility'] ||
          props.primaryHandheldPassives['Elemental Susceptibility'] ||
          props.wornArmorPassives['Elemental Susceptibility']
        immunity =
          immunity ||
          props.traits['Elemental Immunity'] ||
          props.statusEffects['Ethereal'] ||
          props.secondaryHandheldPassives['Elemental Immunity'] ||
          props.primaryHandheldPassives['Elemental Immunity'] ||
          props.wornArmorPassives['Elemental Immunity']
        vulnerability =
          vulnerability ||
          props.traits['Elemental Vulnerability'] ||
          props.secondaryHandheldPassives['Elemental Vulnerability'] ||
          props.primaryHandheldPassives['Elemental Vulnerability'] ||
          props.wornArmorPassives['Elemental Vulnerability']
        damageReduction =
          damageReduction ||
          props.traits['Elemental Damage Reduction'] ||
          props.secondaryHandheldPassives['Elemental Damage Reduction'] ||
          props.primaryHandheldPassives['Elemental Damage Reduction'] ||
          props.wornArmorPassives['Elemental Damage Reduction']
        damageAmplification =
          damageAmplification ||
          props.traits['Elemental Damage Amplification'] ||
          props.secondaryHandheldPassives['Elemental Damage Amplification'] ||
          props.primaryHandheldPassives['Elemental Damage Amplification'] ||
          props.wornArmorPassives['Elemental Damage Amplification']
      }

      if (
        damageType == 'Toxic' ||
        damageType == 'Stress' ||
        damageType == 'Blunt' ||
        damageType == 'Piercing' ||
        damageType == 'Edged'
      ) {
        resistance =
          resistance ||
          props.traits['Mundane Resistance'] ||
          props.secondaryHandheldPassives['Mundane Resistance'] ||
          props.primaryHandheldPassives[damageType + 'Mundane Resistance'] ||
          props.wornArmorPassives[damageType + 'Mundane Resistance']
        susceptibility =
          susceptibility ||
          props.traits['Mundane Susceptibility'] ||
          props.secondaryHandheldPassives['Mundane Susceptibility'] ||
          props.primaryHandheldPassives['Mundane Susceptibility'] ||
          props.wornArmorPassives['Mundane Susceptibility']
        immunity =
          immunity ||
          props.traits['Mundane Immunity'] ||
          props.statusEffects['Ethereal'] ||
          props.secondaryHandheldPassives['Mundane Immunity'] ||
          props.primaryHandheldPassives['Mundane Immunity'] ||
          props.wornArmorPassives['Mundane Immunity']
        vulnerability =
          vulnerability ||
          props.traits['Mundane Vulnerability'] ||
          props.secondaryHandheldPassives['Mundane Vulnerability'] ||
          props.primaryHandheldPassives['Mundane Vulnerability'] ||
          props.wornArmorPassives['Mundane Vulnerability']
        damageReduction =
          damageReduction ||
          props.traits['Mundane Damage Reduction'] ||
          props.secondaryHandheldPassives['Mundane Damage Reduction'] ||
          props.primaryHandheldPassives['Mundane Damage Reduction'] ||
          props.wornArmorPassives['Mundane Damage Reduction']
        damageAmplification =
          damageAmplification ||
          props.traits['Mundane Damage Amplification'] ||
          props.secondaryHandheldPassives['Mundane Damage Amplification'] ||
          props.primaryHandheldPassives['Mundane Damage Amplification'] ||
          props.wornArmorPassives['Mundane Damage Amplification']
      }

      let damageAmp = 0
      let damageRed = 0
      console.log(damageReduction)

      if (damageAmplification) {
        damageAmp += parseInt(dice.value + '') * damageAmplification.number
      }
      if (damageReduction) {
        damageRed -= parseInt(dice.value + '') * damageReduction.number
      }
      if (immunity && !vulnerability) {
        return 0
      }
      if (vulnerability) {
        return damage * 2 + damageAmp
      }
      if (resistance && susceptibility) {
        return Math.max(damage + damageAmp + damageRed, 0)
      }
      if (resistance) {
        return Math.max(Math.floor(damage / 2) + damageAmp + damageRed, 0)
      }
      if (susceptibility) {
        return Math.max(Math.floor(damage * 2) + damageAmp + damageRed, 0)
      }
      return Math.max(Math.floor(damage) + damageAmp + damageRed, 0)
    }

    function dealDamageIgnoreShield() {
      let finalCurrent = props.currentHp

      finalCurrent = finalCurrent - damage.value
      props.setCurrentAndBarrier(finalCurrent, props.barrierHp)
    }
    function applyHeal() {
      let finalCurrent: number = props.currentHp

      finalCurrent = finalCurrent + parseInt(heal.value + '')
      if (finalCurrent > props.totalHp) {
        finalCurrent = props.totalHp
      }
      props.setCurrentAndBarrier(finalCurrent, props.barrierHp)
    }
    function applyHealWithOvershield() {
      let finalCurrent = props.currentHp
      let finalBarrier = props.barrierHp

      finalCurrent = finalCurrent + parseInt(heal.value + '')
      if (finalCurrent > props.totalHp) {
        finalBarrier += finalCurrent - props.totalHp
        finalCurrent = props.totalHp
      }
      props.setCurrentAndBarrier(finalCurrent, finalBarrier)
    }
    function applyBarrier() {
      let finalCurrent = props.currentHp
      let finalBarrier = props.barrierHp

      finalBarrier = finalBarrier + parseInt(barrier.value + '')

      props.setCurrentAndBarrier(finalCurrent, finalBarrier)
    }
    function setNewCurrentBarrierValue() {
      let finalCurrent = props.currentHp

      props.setCurrentAndBarrier(finalCurrent, parseInt(currentBarrierCopy.value + ''))
    }
    function setNewCurrentHpValue(val = currentHpCopy.value) {
      let finalBarrier = props.barrierHp

      props.setCurrentAndBarrier(Math.min(parseInt(val + ''), props.totalHp), finalBarrier)
    }

    function removeModifier(modifierType, modAmount, linkedStatus) {
      props.removeHpStatusModifier({
        modifierType: modifierType,
        modAmount: modAmount,
        linkedStatus: linkedStatus,
        sufferingDamageType: 'no'
      })
    }
    return {
      removeModifier,
      designStore,
      modal,
      userStore,
      damage,
      heal,
      damageType,
      modifierType,
      dealDamage,
      dealDamageIgnoreShield,
      applyHeal,
      applyHealWithOvershield,
      barrier,
      applyBarrier,
      currentHpCopy,
      currentBarrierCopy,
      setNewCurrentBarrierValue,
      setNewCurrentHpValue,
      addHpStatusModifier,
      statusModifiersList,
      getCurrentColor,
      getBarrierColor,
      getTotalColor,
      dice,
      damageTypes,
      props
    }
  },
  components: {
    CustomModal,
    BasicInput,
    BForm,
    HpWidgetModal
  }
}
</script>

<template>
  <div>
    <div
      class="hoverableIconOnSidebar"
      @click="modal = !modal"
      v-if="props.simplifyView"
      style="font-size: x-large; margin: 1rem; text-align: center; cursor: pointer"
    >
      <div style="position: relative">
        <div style="align-self: center; display: flex; justify-content: center">
          <div class="simpleHeart" :style="{ color: designStore.primaryTheme }">
            <i class="bi bi-heart-fill"></i>
          </div>
          <div class="simpleHeartBack" :style="{ color: designStore.secondaryTheme }">
            <i class="bi bi-heart-fill"></i>
          </div>
        </div>
        <div class="simpleHeartText" :style="{ color: designStore.primaryText }">
          {{ currentHp + barrierHp }}
        </div>
      </div>
    </div>
    <div
      v-else
      style="width: 13.25rem; height: 10rem"
      class="hoverableIconOnSidebar"
      :style="{ fontFamily: designStore.font }"
      @click="modal = !modal"
    >
      <div
        style="
          position: absolute;
          font-size: 9rem;
          z-index: 5;
          margin-left: 2rem;
          margin-top: -1.5rem;
          transform: scale(1.2, 1);
        "
        :style="{ color: designStore.primaryTheme }"
      >
        <i class="bi bi-heart-fill"></i>
      </div>
      <div
        style="
          position: absolute;
          font-size: 10rem;
          z-index: 4;
          margin-left: 1.5rem;
          margin-top: -2rem;
          transform: scale(1.05, 1);
        "
        :style="{ color: designStore.secondaryTheme }"
      >
        <i class="bi bi-heart-fill"></i>
      </div>

      <div class="heartTxt">
        <div style="display: flex; flex-direction: column; margin-top: 0.75rem">
          <div style="display: flex; flex-direction: row">
            <div
              style="
                width: 50%;
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                position: relative;
                right: 1rem;
              "
            >
              <div
                style="
                  display: flex;
                  flex-direction: column;
                  align-self: center;
                  margin-bottom: 0.75rem;
                  font-size: xx-large;
                "
                :style="{ color: getCurrentColor() }"
              >
                <div
                  style="align-self: center; margin: 0.5rem; margin-bottom: -1rem"
                  class="bigText"
                >
                  {{ currentHp }}
                </div>
                <div
                  style="align-self: center; font-size: 0.75rem; padding-top: 0.15rem"
                  class="liltext"
                >
                  Current
                </div>
              </div>
            </div>
            <div style="display: flex; justify-content: center; position: absolute; width: 100%">
              <i
                class="bi bi-slash-lg"
                style="
                  align-self: center;
                  font-size: 3rem;
                  transform: skew(25deg);
                  margin-right: -0.75rem;
                  margin-left: -0.5rem;
                "
                :style="{ color: designStore.secondaryTheme }"
              ></i>
            </div>
            <div
              style="
                width: 50%;
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                position: relative;
                left: 1rem;
              "
            >
              <div
                style="
                  display: flex;
                  flex-direction: column;
                  align-self: center;
                  margin-bottom: 0.75rem;
                  font-size: xx-large;
                "
                :style="{ color: getTotalColor() }"
              >
                <div
                  style="align-self: center; margin: 0.5rem; margin-bottom: -1rem"
                  class="bigText"
                >
                  {{ props.totalHp }}
                </div>
                <div
                  style="align-self: center; font-size: 0.75rem; padding-top: 0.15rem"
                  class="liltext"
                >
                  Total
                </div>
              </div>
            </div>
          </div>
          <div
            :style="{ fontFamily: designStore.titleFont, color: designStore.primaryText }"
            style="font-size: 2.25rem; align-self: center; margin-top: -1rem"
          >
            HP
          </div>
        </div>
      </div>
      <div
        style="position: relative; z-index: 5; top: 4rem; left: 0rem; transform: scale(1.1, 1)"
        :style="{ color: getBarrierColor() }"
      >
        <div style="position: absolute; z-index: 6">
          <v-icon
            name="gi-hearts"
            scale="2.9"
            style="
              position: relative;
              top: 1.1rem;
              left: 0.95rem;
              z-index: 4;
              transform: scale(1, 0.95);
            "
            :style="{ color: designStore.primaryTheme }"
          ></v-icon>
        </div>

        <div style="position: absolute; z-index: 5">
          <v-icon
            name="gi-heart-shield"
            scale="3.9"
            style="position: relative; top: 0.35rem; left: 0.35rem; z-index: 4"
            :style="{ color: designStore.primaryTheme }"
          ></v-icon>
        </div>

        <v-icon name="gi-heart-shield" scale="4.5" style="position: relative; z-index: 5"></v-icon>
        <div
          style="
            position: relative;
            z-index: 6;
            width: 4rem;
            height: 3rem;
            left: 0.7rem;
            bottom: 3.6rem;
            display: flex;
            justify-content: center;
            font-size: x-large;
          "
          :style="{ color: designStore.primaryText }"
          id="mememememe"
        >
          <div style="z-index: 6; width: min-content; position: absolute">
            {{ barrierHp }}
          </div>
        </div>
      </div>
    </div>
    <HpWidgetModal
      v-if="!props.isEditing"
      :setCurrentAndBarrier="props.setCurrentAndBarrier"
      :totalHp="props.totalHp"
      :traits="props.traits"
      :secondaryHandheldPassives="secondaryHandheldPassives"
      :primaryHandheldPassives="primaryHandheldPassives"
      :wornArmorPassives="wornArmorPassives"
      :current-hp="props.currentHp"
      :barrier-hp="props.barrierHp"
      :hpStatusModifiers="hpStatusModifiers"
      :statusEffects="props.statusEffects"
      :removeHpStatusModifier="props.removeHpStatusModifier"
      :addNewHpStatusModifier="props.addNewHpStatusModifier"
      :setHp="props.setHp"
      :modal="modal"
      :setModal="(newVal) => (modal = newVal)"
    ></HpWidgetModal>
    <CustomModal
      v-if="props.isEditing"
      title="Modify HP"
      :showModal="modal"
      @close="modal = !modal"
    >
      <template v-slot:body>
        <BForm>
          <BasicInput
            style="margin-bottom: 1rem"
            label="Total Hp"
            type="number"
            :value="props.totalHp"
            :min="0"
            :max="999"
            @newValue="(val) => props.setHp(props.currentHp, val)"
          ></BasicInput>
          <BasicInput
            label="Current Hp"
            type="number"
            :value="props.currentHp"
            :min="0"
            :max="999"
            @newValue="(val) => props.setHp(val, props.totalHp)"
          ></BasicInput>
        </BForm>
      </template>
    </CustomModal>
  </div>
</template>

<style scoped>
.heartTxt {
  position: absolute;
  font-size: 2rem;
  z-index: 5;
  width: 13rem;
}
.num {
  width: 5rem;
  border-radius: 0;
}
.damageInput {
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
}
.damageType {
  min-width: 9rem;
  border-radius: 0;
  border: 2px solid;
  border-right: 1px solid;
  border-left: 1px solid;
}
.damageDice {
  min-width: 5rem;
  border-radius: 0;
  border: 2px solid;
  border-right: 1px solid;
  border-left: 1px solid;
}
.rightField {
  border: 2px solid;
  border-right: 0px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.leftDamageButton {
  min-width: 5rem;
  padding-top: 0.5rem;
  padding-left: 0.5rem;
  border: 2px solid;
  border-left: 1px solid;
  border-right: 0px solid;
  text-align: center;
  border-radius: 0;
  max-height: 2.5rem;
  align-self: end;
}
.rightDamageButton {
  border: 2px solid;
  padding-top: 0.5rem;
  padding-left: 0.5rem;
  border-left: 1px solid;
  width: 12.5rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  max-height: 2.5rem;
  align-self: end;
  flex-grow: 1;
  min-width: 9rem;
}
.superMobile {
  display: none;
}
.simpleHeart {
  position: absolute;
  font-size: 4rem;
  z-index: 3;
  transform: scale(1.2, 1);
  margin-top: -1rem;
}
.simpleHeartBack {
  position: absolute;
  font-size: 4rem;
  z-index: 2;
  transform: scale(1.15, 1.1);
  margin-top: -1rem;
}
.simpleHeartText {
  margin-left: 1rem;
  margin-right: 1rem;
  z-index: 5;
  position: relative;
  padding-top: 0.1rem;
  font-size: x-large;
}
.infoHeader {
  flex-grow: 1;
}
@media (min-width: 1800px) {
  .simpleHeartText {
    z-index: 5;
    position: relative;
    padding-top: 1.5rem;
    font-size: 3rem;
    margin-left: 4rem;
    margin-right: 4rem;
  }
  .simpleHeartBack {
    position: absolute;
    font-size: 8rem;
    z-index: 2;
    transform: scale(1.15, 1.1);
    margin-top: -1rem;
  }
  .simpleHeart {
    position: absolute;
    font-size: 8rem;
    z-index: 3;
    transform: scale(1.2, 1);
    margin-top: -1rem;
  }
}
@media (max-width: 950px) {
  .damageInput {
    flex-wrap: wrap;
  }
  .rightField {
    border-bottom-left-radius: 0;
  }
  .leftDamageButton {
    min-width: 5rem;
    padding-top: 0.5rem;
    padding-left: 0.5rem;
    border-radius: 0;
    flex-grow: 1;
    border: 2px solid;
    border-top: 0;
    border-right: 0px solid;
    border-bottom-left-radius: 0.375rem;
    text-align: center;
    max-height: 2.5rem;
    align-self: end;
  }
  .rightDamageButton {
    border-top: 0;
    border-top-right-radius: 0rem;
    min-width: 8rem;
  }
  .damageType {
    max-width: 15rem;
  }
  @media (max-width: 720px) {
    .liltext {
      display: none;
    }
    .bigText {
      font-size: 2.5rem;
    }
    .damageType {
      max-width: 15rem;
    }
    .rightField {
      border-bottom-left-radius: 0;
    }
    .buttonSpacers {
      display: none;
    }
    .infoHeader {
      font-size: small;
    }

    @media (max-width: 600px) {
      .heart {
        margin-left: -5rem;
      }
      .damageType {
        min-width: 7.25rem;
        font-size: small;
      }
      .rightField {
        font-size: small;

        border-bottom-left-radius: 0;
      }
      .damageDice {
        min-width: 3.5rem;
        font-size: small;
      }

      @media (max-width: 550px) {
        .damageInput {
          margin-left: -1rem;
          margin-right: -1rem;
        }
        .expandingInput {
          margin-left: -1rem;
          margin-right: -1rem;
        }
      }
      .superMobile {
        display: block;
      }
      .norm {
        display: none;
      }
      .infoHeader {
        text-align: center;
      }
    }
  }
}
</style>
